const AUTH_TOKEN_STORAGE_KEY = 'pinday_auth_token'

function isBrowser() {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

function normalizeToken(token: string | null | undefined): string | null {
  if (!token) {
    return null
  }

  const trimmed = token.trim()
  if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
    return null
  }

  return trimmed
}

export function getStoredAuthToken(): string | null {
  if (!isBrowser()) {
    return null
  }

  return normalizeToken(localStorage.getItem(AUTH_TOKEN_STORAGE_KEY))
}

export function setStoredAuthToken(token: string | null | undefined): void {
  if (!isBrowser()) {
    return
  }

  const normalizedToken = normalizeToken(token)
  if (!normalizedToken) {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
    return
  }

  localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, normalizedToken)
}

export function clearStoredAuthToken(): void {
  if (!isBrowser()) {
    return
  }

  localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
}

function toAbsoluteUrl(url: string, origin: string): string {
  try {
    return new URL(url, origin).toString()
  } catch {
    return url
  }
}

function shouldAttachAuthHeader(url: string, apiBaseUrl: string, origin: string): boolean {
  const resolvedUrl = toAbsoluteUrl(url, origin)
  const resolvedApiBase = toAbsoluteUrl(apiBaseUrl, origin).replace(/\/+$/, '')

  return resolvedUrl === resolvedApiBase || resolvedUrl.startsWith(`${resolvedApiBase}/`)
}

let isFetchInterceptorInstalled = false

export function installAuthFetchInterceptor(apiBaseUrl: string): void {
  if (!isBrowser() || isFetchInterceptorInstalled) {
    return
  }

  const originalFetch = window.fetch.bind(window)
  const origin = window.location.origin

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = getStoredAuthToken()
    if (!token) {
      return originalFetch(input, init)
    }

    const url = typeof input === 'string' || input instanceof URL ? String(input) : input.url
    if (!shouldAttachAuthHeader(url, apiBaseUrl, origin)) {
      return originalFetch(input, init)
    }

    const headers = new Headers(input instanceof Request ? input.headers : undefined)
    if (init?.headers) {
      const initHeaders = new Headers(init.headers)
      initHeaders.forEach((value, key) => {
        headers.set(key, value)
      })
    }

    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    if (input instanceof Request) {
      return originalFetch(new Request(input, { ...init, headers }))
    }

    return originalFetch(input, { ...init, headers })
  }

  isFetchInterceptorInstalled = true
}
